import { useState, useEffect } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import { partnersGet, partnersCreate, partnersAddClient } from '../../lib/adminApi';

const ORANGE = '#F37428';
const NAVY   = '#0a2540';

function fmt$(n) {
  return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function Partners() {
  const [partners, setPartners]   = useState([]);
  const [month, setMonth]         = useState('');
  const [loading, setLoading]     = useState(true);
  const [expanded, setExpanded]   = useState({});

  // New partner modal
  const [showNewPartner, setShowNewPartner] = useState(false);
  const [npName, setNpName]   = useState('');
  const [npEmail, setNpEmail] = useState('');
  const [npRate, setNpRate]   = useState('0.10');
  const [npSaving, setNpSaving] = useState(false);

  // New client modal
  const [addingClientTo, setAddingClientTo] = useState(null); // partner object
  const [ncName, setNcName]   = useState('');
  const [ncDesc, setNcDesc]   = useState('');
  const [ncSaving, setNcSaving] = useState(false);
  const [newKey, setNewKey]     = useState(null);
  const [copied, setCopied]     = useState(false);

  const months = [];
  for (let i = 0; i < 6; i++) {
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }

  const selectedMonth = month || months[0];

  async function load(m) {
    setLoading(true);
    try {
      const r = await partnersGet(m || selectedMonth);
      setPartners(r?.partners || []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function handleMonthChange(m) {
    setMonth(m);
    load(m);
  }

  function toggleExpand(pid) {
    setExpanded(prev => ({ ...prev, [pid]: !prev[pid] }));
  }

  async function handleCreatePartner() {
    if (!npName.trim()) return;
    setNpSaving(true);
    try {
      const r = await partnersCreate({ company_name: npName.trim(), contact_email: npEmail.trim() || null, rate_per_enrichment: parseFloat(npRate) || 0.10 });
      if (r?.partner) {
        setShowNewPartner(false);
        setNpName(''); setNpEmail(''); setNpRate('0.10');
        await load();
      } else alert(r?.error || 'Failed');
    } finally { setNpSaving(false); }
  }

  async function handleAddClient() {
    if (!ncName.trim() || !addingClientTo) return;
    setNcSaving(true);
    try {
      const r = await partnersAddClient(addingClientTo.partner_id, { company_name: ncName.trim(), key_description: ncDesc.trim() || null });
      if (r?.key_value) {
        setNewKey(r);
        setNcName(''); setNcDesc('');
        setAddingClientTo(null);
        await load();
      } else alert(r?.error || 'Failed');
    } catch (e) {
      alert(e?.message || 'Request failed');
    } finally { setNcSaving(false); }
  }

  function handleCopy(val) {
    navigator.clipboard.writeText(val).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  }

  const totalEnrichments = partners.reduce((s, p) => s + p.month_ok_calls, 0);
  const totalCharge      = partners.reduce((s, p) => s + p.month_charge, 0);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f0f5fb', fontFamily: "'Inter', system-ui, sans-serif" }}>
      <AdminSidebar active="partners" />
      <main style={{ flex: 1, overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #0f3460 100%)`, padding: '28px 36px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#fff' }}>Partners</h1>
              <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
                Manage partners and their clients · enrichment billing
              </p>
            </div>
            <button onClick={() => setShowNewPartner(true)} style={{
              background: ORANGE, color: '#fff', border: 'none', borderRadius: 8,
              padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>+ New Partner</button>
          </div>
        </div>

        <div style={{ padding: '28px 36px' }}>

          {/* New key success banner */}
          {newKey && (
            <div style={{ background: 'rgba(5,150,105,0.06)', border: '1px solid rgba(5,150,105,0.25)', borderRadius: 12, padding: '16px 20px', marginBottom: 24 }}>
              <div style={{ fontWeight: 700, color: '#065f46', fontSize: 13, marginBottom: 8 }}>
                ✓ Client added — {newKey.company_name} under {newKey.partner_name}
              </div>
              <div style={{ fontSize: 12, color: '#374151', marginBottom: 8 }}>API Key (copy now — shown once):</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <code style={{ flex: 1, fontSize: 11, background: '#0f172a', color: '#e2e8f0', padding: '8px 12px', borderRadius: 6, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {newKey.key_value}
                </code>
                <button onClick={() => handleCopy(newKey.key_value)} style={{
                  background: copied ? '#059669' : ORANGE, color: '#fff', border: 'none', borderRadius: 6,
                  padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                }}>{copied ? '✓ Copied' : 'Copy'}</button>
              </div>
              <button onClick={() => setNewKey(null)} style={{ marginTop: 10, fontSize: 11, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Dismiss</button>
            </div>
          )}

          {/* Month selector + summary */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
            <select value={selectedMonth} onChange={e => handleMonthChange(e.target.value)}
              style={{ padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff', color: NAVY, fontWeight: 600 }}>
              {months.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <div style={s.statCard}>
              <div style={s.statLabel}>Total Enrichments</div>
              <div style={s.statValue}>{totalEnrichments.toLocaleString()}</div>
            </div>
            <div style={s.statCard}>
              <div style={s.statLabel}>Total Charges</div>
              <div style={{ ...s.statValue, color: ORANGE }}>{fmt$(totalCharge)}</div>
            </div>
            <div style={s.statCard}>
              <div style={s.statLabel}>Partners</div>
              <div style={s.statValue}>{partners.length}</div>
            </div>
          </div>

          {/* Partner cards */}
          {loading ? (
            <div style={{ color: '#94a3b8', textAlign: 'center', padding: 40 }}>Loading…</div>
          ) : partners.length === 0 ? (
            <div style={{ color: '#94a3b8', textAlign: 'center', padding: 40 }}>No partners yet — create one above</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {partners.map(p => (
                <div key={p.partner_id} style={s.card}>
                  {/* Partner header row */}
                  <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: expanded[p.partner_id] ? '1px solid #f1f5f9' : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1 }}>
                      <button onClick={() => toggleExpand(p.partner_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#64748b', padding: 0, width: 20 }}>
                        {expanded[p.partner_id] ? '▾' : '▸'}
                      </button>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 15, color: NAVY }}>{p.company_name}</div>
                        {p.contact_email && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{p.contact_email}</div>}
                      </div>
                      <div style={{ marginLeft: 20, display: 'flex', gap: 24 }}>
                        <div>
                          <div style={s.statLabel}>Clients</div>
                          <div style={{ fontSize: 15, fontWeight: 600, color: '#374151' }}>{p.clients.length}</div>
                        </div>
                        <div>
                          <div style={s.statLabel}>Enrichments ({selectedMonth})</div>
                          <div style={{ fontSize: 15, fontWeight: 600, color: '#374151' }}>{p.month_ok_calls.toLocaleString()}</div>
                        </div>
                        <div>
                          <div style={s.statLabel}>Charge</div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: p.month_charge > 0 ? NAVY : '#94a3b8' }}>{fmt$(p.month_charge)}</div>
                        </div>
                        <div>
                          <div style={s.statLabel}>Rate</div>
                          <div style={{ fontSize: 13, color: '#64748b' }}>{fmt$(p.rate_per_enrichment)}/record</div>
                        </div>
                      </div>
                    </div>
                    <button onClick={() => { setAddingClientTo(p); setNcName(''); setNcDesc(''); }} style={{
                      background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 7,
                      padding: '6px 14px', fontSize: 12, fontWeight: 600, color: NAVY, cursor: 'pointer',
                    }}>+ Add Client</button>
                  </div>

                  {/* Expanded client list */}
                  {expanded[p.partner_id] && (
                    <div>
                      {p.clients.length === 0 ? (
                        <div style={{ padding: '16px 20px 16px 56px', fontSize: 13, color: '#94a3b8' }}>No clients yet — add one above</div>
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ background: '#f8fafc' }}>
                              {['Client', 'Key Description', 'Key Status', 'Enrichments', 'Failed', 'Charge'].map(h => (
                                <th key={h} style={{ ...s.th, paddingLeft: h === 'Client' ? 56 : 16 }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {p.clients.map(c => (
                              <tr key={c.tenant_id} style={{ borderTop: '1px solid #f1f5f9' }}>
                                <td style={{ ...s.td, paddingLeft: 56 }}>
                                  <div style={{ fontWeight: 600, color: '#1e293b' }}>{c.company_name}</div>
                                  <div style={{ fontSize: 10, color: '#94a3b8' }}>tenant #{c.tenant_id}</div>
                                </td>
                                <td style={{ ...s.td, fontSize: 12, color: '#64748b' }}>{c.key_description || <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                                <td style={s.td}>
                                  {c.key_id ? (
                                    <span style={{ fontSize: 11, fontWeight: 600, color: c.key_active ? '#059669' : '#94a3b8' }}>
                                      {c.key_active ? '● Active' : '○ Inactive'}
                                    </span>
                                  ) : <span style={{ color: '#94a3b8', fontSize: 11 }}>No key</span>}
                                </td>
                                <td style={{ ...s.td, fontWeight: 600 }}>{c.ok_calls.toLocaleString()}</td>
                                <td style={{ ...s.td, color: (c.total_calls - c.ok_calls) > 0 ? '#dc2626' : '#94a3b8' }}>
                                  {(c.total_calls - c.ok_calls).toLocaleString()}
                                </td>
                                <td style={{ ...s.td, fontWeight: 700, color: NAVY }}>{fmt$(c.ok_calls * p.rate_per_enrichment)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* New Partner Modal */}
      {showNewPartner && (
        <Modal title="New Partner" onClose={() => setShowNewPartner(false)}>
          <div style={{ marginBottom: 14 }}>
            <label style={s.label}>Company Name *</label>
            <input value={npName} onChange={e => setNpName(e.target.value)} placeholder="e.g. Sourcing Insights"
              style={{ ...s.input, width: '100%' }} autoFocus />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={s.label}>Contact Email</label>
            <input value={npEmail} onChange={e => setNpEmail(e.target.value)} placeholder="billing@partner.com"
              style={{ ...s.input, width: '100%' }} />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={s.label}>Rate per Enrichment ($)</label>
            <input value={npRate} onChange={e => setNpRate(e.target.value)} type="number" step="0.01" placeholder="0.10"
              style={{ ...s.input, width: 100 }} />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={handleCreatePartner} disabled={npSaving || !npName.trim()} style={s.btnPrimary}>
              {npSaving ? 'Creating…' : 'Create Partner'}
            </button>
            <button onClick={() => setShowNewPartner(false)} style={s.btnSecondary}>Cancel</button>
          </div>
        </Modal>
      )}

      {/* Add Client Modal */}
      {addingClientTo && (
        <Modal title={`Add Client — ${addingClientTo.company_name}`} onClose={() => setAddingClientTo(null)}>
          <div style={{ marginBottom: 14 }}>
            <label style={s.label}>Client Company Name *</label>
            <input value={ncName} onChange={e => setNcName(e.target.value)} placeholder="e.g. Acme Corp"
              style={{ ...s.input, width: '100%' }} autoFocus />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={s.label}>Key Description</label>
            <input value={ncDesc} onChange={e => setNcDesc(e.target.value)} placeholder="e.g. Acme — SAP n8n integration"
              style={{ ...s.input, width: '100%' }} />
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 20, background: '#f8fafc', borderRadius: 8, padding: '10px 14px' }}>
            This will create a new tenant and generate an API key. Share the key with {addingClientTo.company_name} to activate enrichment.
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={handleAddClient} disabled={ncSaving || !ncName.trim()} style={s.btnPrimary}>
              {ncSaving ? 'Creating…' : 'Create Client & Generate Key'}
            </button>
            <button onClick={() => setAddingClientTo(null)} style={s.btnSecondary}>Cancel</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: '24px 28px', width: 480, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0a2540' }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, color: '#94a3b8', cursor: 'pointer' }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

const s = {
  card:        { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' },
  th:          { fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '8px 16px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' },
  td:          { padding: '11px 16px', verticalAlign: 'middle', fontSize: 13 },
  label:       { display: 'block', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 },
  input:       { padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff', color: '#1e293b', boxSizing: 'border-box' },
  btnPrimary:  { background: ORANGE, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnSecondary:{ background: '#fff', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 8, padding: '9px 16px', fontSize: 13, cursor: 'pointer' },
  statCard:    { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 18px', minWidth: 130 },
  statLabel:   { fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 },
  statValue:   { fontSize: 20, fontWeight: 700, color: '#0a2540' },
};
